import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft } from 'lucide-react';
import { serviceTicketsApi } from '../api/serviceTickets';
import { coaApi } from '../api/coa';
import api from '../api/client';
import { useToast } from '../hooks/useToast';
import ProductPicker from '../components/ProductPicker';
import ServicePicker from '../components/ServicePicker';
import DocumentHeader from '../components/DocumentHeader';

interface Props { ticketId: number; onBack: () => void; }
const colorMap: Record<string,string> = { blue:'badge-blue', green:'badge-green', amber:'badge-amber', gray:'badge-gray', red:'badge-red' };

const TicketDetail: React.FC<Props> = ({ ticketId, onBack }) => {
  const { showToast } = useToast();
  const [t, setT] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [partForm, setPartForm] = useState({ product_id:'', product_name:'', location_id:'', qty:'1', unit_price:'0' });
  const [labourForm, setLabourForm] = useState({ product_id:'', name:'', amount:'' });
  const [services, setServices] = useState<any[]>([]);
  const [noteText, setNoteText] = useState('');
  const [showCollect, setShowCollect] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [printMode, setPrintMode] = useState<'job'|'quote'>('job');
  const [showApprove, setShowApprove] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ diagnosis:'', required_parts:'', estimated_cost:'' });
  const [approvalNote, setApprovalNote] = useState('');
  const [depositAmt, setDepositAmt] = useState('');
  const [depositAcct, setDepositAcct] = useState('');
  const [resolution, setResolution] = useState('');
  const [collectForm, setCollectForm] = useState({ sale_type:'cash', account_id:'', collect_date:new Date().toISOString().split('T')[0] });

  const load = useCallback(() => {
    serviceTicketsApi.get(ticketId).then(r => { setT(r.data.data); setLoading(false); }).catch(() => setLoading(false));
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/settings/locations').then(r => setLocations(r.data.data || [])).catch(() => {});
    api.get('/products', { params:{ limit: 500 } }).then(r => setServices((r.data.data||[]).filter((p:any) => p.product_type === 'service'))).catch(() => {});
    coaApi.list().then(r => setAccounts((r.data.data||[]).filter((a:any) => String(a.account_code).startsWith('1')))).catch(() => {});
  }, []);

  const fmt = (n:any) => Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});

  const addPart = async () => {
    if (!partForm.product_id || !partForm.location_id) { showToast('Product and location required','error'); return; }
    setSaving(true);
    try {
      const res = await serviceTicketsApi.addPart(ticketId, {
        product_id:Number(partForm.product_id), location_id:Number(partForm.location_id),
        qty:Number(partForm.qty), unit_price:Number(partForm.unit_price),
      });
      showToast('Part added');
      if (res.data.data?.stock_warning) setTimeout(() => showToast('Stock went negative - ' + res.data.data.stock_warning, 'error'), 600);
      setPartForm({ product_id:'', product_name:'', location_id:'', qty:'1', unit_price:'0' });
      load();
    } catch (e:any) { showToast(e.response?.data?.message || 'Failed','error'); }
    finally { setSaving(false); }
  };

  const addLabour = async () => {
    if (!labourForm.product_id || !labourForm.amount) { showToast('Select a service and amount','error'); return; }
    setSaving(true);
    try {
      await serviceTicketsApi.addLabour(ticketId, { product_id:Number(labourForm.product_id), amount:Number(labourForm.amount) });
      showToast('Labour added'); setLabourForm({ product_id:'', name:'', amount:'' }); load();
    } catch (e:any) { showToast(e.response?.data?.message || 'Failed','error'); }
    finally { setSaving(false); }
  };

  const saveDiagnosis = async () => {
    if (!quoteForm.diagnosis.trim()) { showToast('Diagnosis is required','error'); return; }
    setSaving(true);
    try {
      await serviceTicketsApi.diagnose(ticketId, { diagnosis:quoteForm.diagnosis, required_parts:quoteForm.required_parts || null });
      showToast('Diagnosis recorded'); setShowQuote(false); load();
      setTimeout(() => showToast('Now add the parts and labour this repair needs'), 700);
    } catch (e:any) { showToast(e.response?.data?.message || 'Failed','error'); }
    finally { setSaving(false); }
  };

  const doSendQuote = async () => {
    setSaving(true);
    try {
      const res = await serviceTicketsApi.sendQuote(ticketId);
      showToast(res.data.message); load();
    } catch (e:any) { showToast(e.response?.data?.message || 'Failed','error'); }
    finally { setSaving(false); }
  };

  const doReject = async () => {
    const reason = window.prompt('Why is the customer declining? (optional)');
    if (reason === null) return;
    setSaving(true);
    try {
      await serviceTicketsApi.reject(ticketId, { reason: reason || null });
      showToast('Quote declined'); load();
    } catch (e:any) { showToast(e.response?.data?.message || 'Failed','error'); }
    finally { setSaving(false); }
  };

  const doCompleteRepair = async () => {
    if (!resolution.trim()) { showToast('Record what was done','error'); return; }
    setSaving(true);
    try {
      const res = await serviceTicketsApi.complete(ticketId, { resolution_note: resolution });
      showToast(res.data.message);
      const w = res.data.data?.stock_warnings || [];
      if (w.length) setTimeout(() => showToast('Stock went negative - ' + w.join('; '), 'error'), 600);
      setShowComplete(false); setResolution(''); load();
    } catch (e:any) { showToast(e.response?.data?.message || 'Failed','error'); }
    finally { setSaving(false); }
  };

  const doPayment = async () => {
    if (!collectForm.account_id) { showToast('Payment account required','error'); return; }
    setSaving(true);
    try {
      await serviceTicketsApi.payment(ticketId, { account_id:Number(collectForm.account_id), amount: Math.max(0, Number(t.total_amount) - Number(t.deposit_amount||0)), payment_date:collectForm.collect_date });
      showToast('Payment recorded'); setShowCollect(false); load();
    } catch (e:any) { showToast(e.response?.data?.message || 'Failed','error'); }
    finally { setSaving(false); }
  };

  const saveQuote = async () => {
    if (!quoteForm.diagnosis.trim()) { showToast('Diagnosis is required','error'); return; }
    if (!quoteForm.estimated_cost) { showToast('Estimated cost is required','error'); return; }
    setSaving(true);
    try {
      await serviceTicketsApi.quote(ticketId, { diagnosis:quoteForm.diagnosis, required_parts:quoteForm.required_parts || null, estimated_cost:Number(quoteForm.estimated_cost) });
      showToast('Quote recorded'); setShowQuote(false); load();
    } catch (e:any) { showToast(e.response?.data?.message || 'Failed','error'); }
    finally { setSaving(false); }
  };

  const doApprove = async () => {
    setSaving(true);
    try {
      if (Number(depositAmt) > 0 && !depositAcct) { showToast('Select the account the deposit was received into','error'); setSaving(false); return; }
      await serviceTicketsApi.approve(ticketId, { approval_note: approvalNote || null, deposit_amount: Number(depositAmt||0), deposit_account_id: depositAcct ? Number(depositAcct) : null });
      showToast('Approval recorded'); setShowApprove(false); setApprovalNote(''); load();
    } catch (e:any) { showToast(e.response?.data?.message || 'Failed','error'); }
    finally { setSaving(false); }
  };

  const doComplete = async () => {
    if (!resolution.trim()) { showToast('Record what was done','error'); return; }
    setSaving(true);
    try {
      await serviceTicketsApi.changeStatus(ticketId, { status:'ticket_ready', resolution_note:resolution });
      showToast('Ticket completed'); setShowComplete(false); setResolution(''); load();
    } catch (e:any) { showToast(e.response?.data?.message || 'Failed','error'); }
    finally { setSaving(false); }
  };

  const doDeliver = async (confirmUnpaid = false) => {
    setSaving(true);
    try {
      await serviceTicketsApi.deliver(ticketId, { confirm_unpaid: confirmUnpaid });
      showToast('Device delivered'); load();
    } catch (e:any) {
      const d = e.response?.data;
      if (d?.requires_confirmation) {
        if (window.confirm(d.message + '\n\nRelease the device without payment?')) { await doDeliver(true); return; }
      } else { showToast(d?.message || 'Failed','error'); }
    }
    finally { setSaving(false); }
  };

  const changeStatus = async (status:string) => {
    setSaving(true);
    try { await serviceTicketsApi.changeStatus(ticketId, { status }); showToast('Status updated'); load(); }
    catch (e:any) { showToast(e.response?.data?.message || 'Failed','error'); }
    finally { setSaving(false); }
  };

  const doCollect = async () => {
    if (collectForm.sale_type === 'cash' && !collectForm.account_id) { showToast('Payment account required','error'); return; }
    setSaving(true);
    try {
      const payload:any = { sale_type: collectForm.sale_type, collect_date: collectForm.collect_date };
      if (collectForm.sale_type === 'cash') payload.splits = [{ account_id:Number(collectForm.account_id), amount:Number(t.total_amount) }];
      const res = await serviceTicketsApi.collect(ticketId, payload);
      showToast(res.data.message); setShowCollect(false); load();
    } catch (e:any) { showToast(e.response?.data?.message || 'Failed','error'); }
    finally { setSaving(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try { await serviceTicketsApi.addNote(ticketId, noteText); setNoteText(''); load(); }
    catch (e:any) { showToast(e.response?.data?.message || 'Failed','error'); }
  };

  if (loading) return <div className="empty-state"><p>Loading...</p></div>;
  if (!t) return <div className="empty-state"><p>Not found.</p></div>;

  const isCollected = ['Completed','Delivered','Rejected'].includes(t.status);
  const showLines = t.status !== 'Open';
  const nextAction = t.status === 'Received' ? { label:'Start repair', status:'ticket_in_progress' }
                   : t.status === 'In Progress' ? { label:'Mark ready', status:'ticket_ready' } : null;

  return (
    <div>
      {showQuote && (
        <div>
          <div className="page-header">
            <div style={{display:'flex',alignItems:'center',gap:12}}>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowQuote(false)}><ArrowLeft size={13} /> Back</button>
              <h1 className="page-title">Diagnose {t.ticket_no}</h1>
            </div>
            <button className="btn btn-primary" onClick={saveDiagnosis} disabled={saving}>{saving ? 'Saving...' : 'Save and quote'}</button>
          </div>

          <div className="card" style={{marginBottom:12}}>
            <div className="card-header"><span>Diagnosis</span></div>
            <div className="card-body">
              <div style={{background:'var(--sur2)',borderRadius:6,padding:'10px 12px',marginBottom:12,fontSize:12}}>
                <strong>{[t.device_brand,t.device_model].filter(Boolean).join(' ')}</strong> — {t.problem_description}
              </div>
              <div className="form-group"><label className="form-label">What is wrong with the device *</label>
                <textarea className="form-control" rows={3} value={quoteForm.diagnosis} onChange={e => setQuoteForm(f=>({...f,diagnosis:e.target.value}))} />
              </div>
              <div className="form-group"><label className="form-label">Notes on parts required</label>
                <input className="form-control" value={quoteForm.required_parts} onChange={e => setQuoteForm(f=>({...f,required_parts:e.target.value}))} placeholder="Optional" />
              </div>
            </div>
          </div>

          <div className="card" style={{marginBottom:12}}>
            <div className="card-header"><span>Parts needed</span></div>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead><tr style={{background:'var(--sur2)'}}>
                <th style={{padding:'6px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Product</th>
                <th style={{padding:'6px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Qty</th>
                <th style={{padding:'6px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Price</th>
                <th style={{padding:'6px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Total</th>
                <th style={{width:30}}></th>
              </tr></thead>
              <tbody>
                {(t.parts||[]).map((p:any) => (
                  <tr key={p.id} style={{borderBottom:'1px solid var(--bdr)'}}>
                    <td style={{padding:'7px 10px',fontSize:12}}>{p.product_name}<div style={{fontSize:11,color:'var(--tx3)'}}>{p.location_name}</div></td>
                    <td className="td-num" style={{padding:'7px 10px'}}>{Number(p.qty)}</td>
                    <td className="td-num" style={{padding:'7px 10px'}}>{fmt(p.unit_price)}</td>
                    <td className="td-num" style={{padding:'7px 10px'}}>{fmt(p.line_total)}</td>
                    <td style={{padding:'7px 4px',textAlign:'center'}}>
                      <button className="btn btn-secondary btn-sm" style={{padding:'2px 6px'}} onClick={async () => { await serviceTicketsApi.removePart(ticketId,p.id); showToast('Removed'); load(); }}>x</button>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td style={{padding:'6px 6px'}}>
                    <ProductPicker value={partForm.product_id} initialLabel={partForm.product_name} locationId={partForm.location_id} fontSize={12}
                      onSelect={(p:any) => setPartForm(f=>({...f, product_id:String(p.id), product_name:p.name, unit_price:String(p.selling_price ?? 0)}))} />
                    <select className="form-control" style={{marginTop:4,fontSize:11}} value={partForm.location_id} onChange={e => setPartForm(f=>({...f,location_id:e.target.value}))}>
                      <option value="">Select location</option>
                      {locations.map((l:any) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </td>
                  <td style={{padding:'6px 4px',width:90}}><input className="form-control mono" style={{textAlign:'right'}} value={partForm.qty} onChange={e => setPartForm(f=>({...f,qty:e.target.value}))} /></td>
                  <td style={{padding:'6px 4px',width:100}}><input className="form-control mono" style={{textAlign:'right'}} value={partForm.unit_price} onChange={e => setPartForm(f=>({...f,unit_price:e.target.value}))} /></td>
                  <td colSpan={2} style={{padding:'6px 4px'}}><button className="btn btn-secondary btn-sm" onClick={addPart} disabled={saving}>Add part</button></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="card" style={{marginBottom:12}}>
            <div className="card-header"><span>Labour</span></div>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <tbody>
                {(t.labour||[]).map((l:any) => (
                  <tr key={l.id} style={{borderBottom:'1px solid var(--bdr)'}}>
                    <td style={{padding:'7px 10px',fontSize:12}}>{l.description}</td>
                    <td className="td-num" style={{padding:'7px 10px',width:110}}>{fmt(l.amount)}</td>
                    <td style={{width:30,textAlign:'center'}}>
                      <button className="btn btn-secondary btn-sm" style={{padding:'2px 6px'}} onClick={async () => { await serviceTicketsApi.removeLabour(ticketId,l.id); showToast('Removed'); load(); }}>x</button>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td style={{padding:'6px 4px'}}>
                    <ServicePicker services={services} value={labourForm.name} onSelect={(svc: any) => setLabourForm(f => ({ ...f, product_id: String(svc.id), name: svc.name, amount: String(svc.selling_price ?? 0) }))} />
                  </td>
                  <td style={{padding:'6px 4px',width:110}}><input className="form-control mono" style={{textAlign:'right'}} value={labourForm.amount} onChange={e => setLabourForm(f=>({...f,amount:e.target.value}))} /></td>
                  <td style={{padding:'6px 4px'}}><button className="btn btn-secondary btn-sm" onClick={addLabour} disabled={saving}>Add</button></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="card">
            <div className="card-body" style={{display:'flex',justifyContent:'flex-end',gap:24}}>
              <div style={{textAlign:'right'}}><div style={{fontSize:11,color:'var(--tx3)'}}>Parts</div><div style={{fontSize:13,fontFamily:'monospace'}}>{fmt(t.parts_cost)}</div></div>
              <div style={{textAlign:'right'}}><div style={{fontSize:11,color:'var(--tx3)'}}>Labour</div><div style={{fontSize:13,fontFamily:'monospace'}}>{fmt(t.service_charge)}</div></div>
              <div style={{textAlign:'right'}}><div style={{fontSize:11,color:'var(--tx3)'}}>Quote total</div><div style={{fontSize:18,fontWeight:700,fontFamily:'monospace'}}>{fmt(t.total_amount)}</div></div>
            </div>
          </div>
        </div>
      )}

      {!showQuote && <>

      {showApprove && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div className="card" style={{width:460}}>
            <div className="card-header"><span>Customer approval — {t.ticket_no}</span><button className="btn btn-secondary btn-sm" onClick={() => setShowApprove(false)}>Close</button></div>
            <div className="card-body">
              <div style={{background:'var(--sur2)',borderRadius:6,padding:'10px 12px',marginBottom:12,fontSize:13}}>
                <div style={{marginBottom:4}}><strong>Diagnosis:</strong> {t.diagnosis}</div>
                {t.required_parts && <div style={{marginBottom:4}}><strong>Parts:</strong> {t.required_parts}</div>}
                <div><strong>Quoted:</strong> {fmt(t.estimated_cost)}</div>
              </div>
              <div style={{fontSize:12,color:'var(--tx3)',marginBottom:12}}>Print the quote for the customer to sign, then record the approval here.</div>
              <div style={{display:'grid',gridTemplateColumns:'130px 1fr',gap:8}}>
                <div className="form-group"><label className="form-label">Deposit (50%)</label>
                  <input className="form-control mono" style={{textAlign:'right'}} value={depositAmt} onChange={e => setDepositAmt(e.target.value)} />
                </div>
                <div className="form-group"><label className="form-label">Received into</label>
                  <select className="form-control" value={depositAcct} onChange={e => setDepositAcct(e.target.value)}>
                    <option value="">Select account</option>
                    {accounts.map((ac:any) => <option key={ac.id} value={ac.id}>{ac.account_code} {ac.account_name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group"><label className="form-label">Note</label>
                <input className="form-control" value={approvalNote} onChange={e => setApprovalNote(e.target.value)} placeholder="Signed at counter" />
              </div>
              <div style={{marginTop:14,display:'flex',gap:8,justifyContent:'flex-end'}}>
                <button className="btn btn-secondary" onClick={() => { setPrintMode('quote'); setShowApprove(false); setTimeout(() => { window.print(); setPrintMode('job'); }, 100); }}>Print agreement</button>
                <button className="btn btn-primary" onClick={doApprove} disabled={saving}>{saving ? 'Saving...' : 'Customer approved'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showComplete && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div className="card" style={{width:460}}>
            <div className="card-header"><span>Complete repair — {t.ticket_no}</span><button className="btn btn-secondary btn-sm" onClick={() => setShowComplete(false)}>Close</button></div>
            <div className="card-body">
              <div className="form-group"><label className="form-label">What was done *</label>
                <textarea className="form-control" rows={3} value={resolution} onChange={e => setResolution(e.target.value)} placeholder="Screen replaced and tested" />
              </div>
              <div style={{fontSize:12,color:'var(--tx3)'}}>Total so far: {fmt(t.total_amount)} {t.estimated_cost > 0 && Number(t.total_amount) > Number(t.estimated_cost) ? ' — above the quoted ' + fmt(t.estimated_cost) : ''}</div>
              <div style={{marginTop:14,display:'flex',gap:8,justifyContent:'flex-end'}}>
                <button className="btn btn-secondary" onClick={() => setShowComplete(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={doCompleteRepair} disabled={saving}>{saving ? 'Posting...' : 'Complete and invoice'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCollect && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}} id="no-print">
          <div className="card" style={{width:420}}>
            <div className="card-header"><span>Collect {t.ticket_no}</span><button className="btn btn-secondary btn-sm" onClick={() => setShowCollect(false)}>Close</button></div>
            <div className="card-body">
              <div style={{fontSize:13,marginBottom:12}}>Total {fmt(t.total_amount)}{Number(t.deposit_amount||0) > 0 ? ' · deposit ' + fmt(t.deposit_amount) : ''} · <strong>balance {fmt(Math.max(0, Number(t.total_amount) - Number(t.deposit_amount||0)))}</strong></div>
              <div className="form-group"><label className="form-label">How is the customer paying?</label>
                <select className="form-control" value={collectForm.sale_type} onChange={e => setCollectForm(f=>({...f,sale_type:e.target.value}))}>
                  <option value="cash">Paying now</option>
                  <option value="invoice">On account — settle later</option>
                </select>
              </div>
              {collectForm.sale_type === 'invoice' && (
                <div style={{background:'var(--sur2)',borderRadius:6,padding:'10px 12px',fontSize:12,color:'var(--tx3)',marginBottom:10}}>
                  The invoice stays outstanding against the customer. The device can still be delivered.
                </div>
              )}
              {collectForm.sale_type === 'cash' && (
                <div className="form-group"><label className="form-label">Received into *</label>
                  <select className="form-control" value={collectForm.account_id} onChange={e => setCollectForm(f=>({...f,account_id:e.target.value}))}>
                    <option value="">Select account</option>
                    {accounts.map((a:any) => <option key={a.id} value={a.id}>{a.account_code} {a.account_name}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group"><label className="form-label">Date</label>
                <input type="date" className="form-control" value={collectForm.collect_date} max={new Date().toISOString().split('T')[0]} onChange={e => setCollectForm(f=>({...f,collect_date:e.target.value}))} style={{width:180}} />
              </div>
              <div style={{marginTop:14,display:'flex',gap:8,justifyContent:'flex-end'}}>
                <button className="btn btn-secondary" onClick={() => setShowCollect(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={() => collectForm.sale_type === 'invoice' ? setShowCollect(false) : doPayment()} disabled={saving}>
                  {saving ? 'Posting...' : (collectForm.sale_type === 'invoice' ? 'Leave on account' : 'Record payment')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="page-header" id="no-print">
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <button className="btn btn-secondary btn-sm" onClick={onBack}><ArrowLeft size={13} /> Back</button>
          <h1 className="page-title">{t.ticket_no}</h1>
          <span className={`badge ${colorMap[t.status_color]||'badge-gray'}`}>{t.status}</span>
          {t.sale_id && <span className="badge badge-green">Paid</span>}
          {t.status === 'Completed' && !t.sale_id && <span className="badge badge-amber">Unpaid</span>}
        </div>
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-secondary" onClick={() => { setPrintMode('quote'); setTimeout(() => { window.print(); setPrintMode('job'); }, 100); }}>Print agreement</button>
          {t.status === 'Open' && <button className="btn btn-primary" onClick={() => { setQuoteForm({ diagnosis:t.diagnosis||'', required_parts:t.required_parts||'', estimated_cost:'' }); setShowQuote(true); }}>Diagnose</button>}
          {t.status === 'Diagnosed' && <button className="btn btn-primary" onClick={doSendQuote} disabled={saving}>Send quote</button>}
          {t.status === 'Quoted' && <button className="btn btn-secondary" onClick={doReject} disabled={saving}>Customer declined</button>}
          {t.status === 'Quoted' && <button className="btn btn-primary" onClick={() => { setDepositAmt((Number(t.estimated_cost||0)/2).toFixed(2)); setShowApprove(true); }}>Customer approved</button>}
          {t.status === 'Approved' && <button className="btn btn-primary" onClick={() => changeStatus('ticket_in_progress')} disabled={saving}>Start repair</button>}
          {t.status === 'In Progress' && <button className="btn btn-primary" onClick={() => { setResolution(t.resolution_note||''); setShowComplete(true); }}>Complete repair</button>}
          {t.status === 'Completed' && !t.paid_at && <button className="btn btn-success" onClick={() => setShowCollect(true)}>Take payment</button>}
          {(t.status === 'Completed' || t.status === 'Rejected') && <button className="btn btn-primary" onClick={() => doDeliver(false)} disabled={saving}>Deliver device</button>}
        </div>
      </div>

      {printMode === 'quote' && (
        <div className="card" id="print-area">
          <DocumentHeader branchName={t.branch_name} branchAddress={t.branch_address} branchPhone={t.branch_phone} branchEmail={t.branch_email} documentTitle={'Qiimaha dayactirka — ' + t.ticket_no} />
          <div style={{padding:'16px'}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
              <div>
                <div style={{fontSize:11,color:'var(--tx3)'}}>Macmiil / Customer</div>
                <div style={{fontWeight:600,fontSize:13}}>{t.customer_name}</div>
                <div style={{fontSize:12}}>{t.customer_mobile}</div>
                {t.alternate_mobile && <div style={{fontSize:12,color:'var(--tx3)'}}>Alt: {t.alternate_mobile}</div>}
              </div>
              <div>
                <div style={{fontSize:11,color:'var(--tx3)'}}>Taariikh / Date</div>
                <div style={{fontWeight:600,fontSize:13}}>{new Date().toLocaleDateString()}</div>
              </div>
            </div>

            <table style={{width:'100%',borderCollapse:'collapse',marginBottom:16}}>
              <tbody>
                <tr style={{borderBottom:'1px solid var(--bdr)'}}>
                  <td style={{padding:'7px 0',fontSize:12,color:'var(--tx3)',width:170}}>Qalabka / Device</td>
                  <td style={{padding:'7px 0',fontSize:13}}>{[t.device_brand,t.device_model].filter(Boolean).join(' ')}</td>
                </tr>
                <tr style={{borderBottom:'1px solid var(--bdr)'}}>
                  <td style={{padding:'7px 0',fontSize:12,color:'var(--tx3)'}}>Serial / IMEI</td>
                  <td style={{padding:'7px 0',fontSize:13,fontFamily:'monospace'}}>{t.device_serial || '-'}</td>
                </tr>
                <tr style={{borderBottom:'1px solid var(--bdr)'}}>
                  <td style={{padding:'7px 0',fontSize:12,color:'var(--tx3)'}}>Cilad la sheegay / Fault</td>
                  <td style={{padding:'7px 0',fontSize:13}}>{t.problem_description}</td>
                </tr>
                <tr style={{borderBottom:'1px solid var(--bdr)'}}>
                  <td style={{padding:'7px 0',fontSize:12,color:'var(--tx3)'}}>Baaris / Diagnosis</td>
                  <td style={{padding:'7px 0',fontSize:13}}>{t.diagnosis || '-'}</td>
                </tr>
                {t.required_parts && (
                  <tr style={{borderBottom:'1px solid var(--bdr)'}}>
                    <td style={{padding:'7px 0',fontSize:12,color:'var(--tx3)'}}>Qeybaha / Parts</td>
                    <td style={{padding:'7px 0',fontSize:13}}>{t.required_parts}</td>
                  </tr>
                )}
                {t.condition_on_intake && (
                  <tr style={{borderBottom:'1px solid var(--bdr)'}}>
                    <td style={{padding:'7px 0',fontSize:12,color:'var(--tx3)'}}>Xaalada / Condition</td>
                    <td style={{padding:'7px 0',fontSize:13}}>{t.condition_on_intake}</td>
                  </tr>
                )}
                {t.accessories && (
                  <tr style={{borderBottom:'1px solid var(--bdr)'}}>
                    <td style={{padding:'7px 0',fontSize:12,color:'var(--tx3)'}}>Agabka / Accessories</td>
                    <td style={{padding:'7px 0',fontSize:13}}>{t.accessories}</td>
                  </tr>
                )}
              </tbody>
            </table>

            <div style={{border:'2px solid var(--tx1)',borderRadius:6,padding:'12px 14px',marginBottom:20,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{fontSize:13,fontWeight:600}}>Qiimaha la qiyaasay / Estimated cost</span>
              <span style={{fontSize:18,fontWeight:700,fontFamily:'monospace'}}>{fmt(t.estimated_cost)}</span>
            </div>

            {Number(t.deposit_amount||0) > 0 && (
              <div style={{display:'flex',justifyContent:'space-between',fontSize:13,marginTop:-12,marginBottom:14,padding:'0 14px'}}>
                <span>Lacag hore / Deposit paid</span><span style={{fontFamily:'monospace',fontWeight:600}}>{fmt(t.deposit_amount)}</span>
              </div>
            )}
            <div style={{fontSize:12,fontWeight:600,marginBottom:6}}>Shuruudaha / Terms and conditions</div>
            <table style={{width:'100%',borderCollapse:'collapse',marginBottom:10}}>
              <tbody>
                {String(t.repair_terms||'').split(/\r?\n/).filter((x:string) => x.trim()).map((line:string, i:number) => {
                  const [so, en] = line.split(' | ');
                  return (
                    <tr key={i} style={{borderBottom:'1px solid var(--bdr)',verticalAlign:'top'}}>
                      <td style={{padding:'4px 8px 4px 0',fontSize:11,width:'50%'}}>{so}</td>
                      <td style={{padding:'4px 0 4px 8px',fontSize:11,color:'var(--tx3)',width:'50%'}}>{en}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{fontSize:12,marginBottom:20,lineHeight:1.5}}>{t.authorisation_text}</div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:40,marginTop:44}}>
              <div>
                <div style={{height:40}}></div>
                <div style={{borderTop:'1px solid var(--tx1)',paddingTop:6,fontSize:12,color:'var(--tx3)'}}>Saxiixa macmiilka / Customer signature</div>
                <div style={{fontSize:11,color:'var(--tx3)',marginTop:10}}>Magaca / Name: ____________________</div>
                <div style={{fontSize:11,color:'var(--tx3)',marginTop:6}}>Taariikhda / Date: __________________</div>
              </div>
              <div>
                <div style={{height:40}}></div>
                <div style={{borderTop:'1px solid var(--tx1)',paddingTop:6,fontSize:12,color:'var(--tx3)'}}>Saxiixa shirkadda / For the company</div>
                <div style={{fontSize:11,color:'var(--tx3)',marginTop:10}}>Magaca / Name: ____________________</div>
                <div style={{fontSize:11,color:'var(--tx3)',marginTop:6}}>Taariikhda / Date: __________________</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card" id="print-area" style={{display: printMode === 'quote' ? 'none' : undefined}}>
        <DocumentHeader branchName={t.branch_name} branchAddress={t.branch_address} branchPhone={t.branch_phone} branchEmail={t.branch_email} documentTitle={'Repair job sheet - ' + t.ticket_no} />

        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:12,padding:'14px 16px'}}>
          <div><div style={{fontSize:11,color:'var(--tx3)'}}>Customer</div><div style={{fontWeight:600,fontSize:13}}>{t.customer_name}</div><div style={{fontSize:12,color:'var(--tx3)'}}>{t.customer_mobile}</div></div>
          <div><div style={{fontSize:11,color:'var(--tx3)'}}>Device</div><div style={{fontWeight:600,fontSize:13}}>{[t.device_brand,t.device_model].filter(Boolean).join(' ')}</div><div style={{fontSize:12,color:'var(--tx3)',fontFamily:'monospace'}}>{t.device_serial}</div></div>
          <div><div style={{fontSize:11,color:'var(--tx3)'}}>Received</div><div style={{fontWeight:600,fontSize:13}}>{t.received_date ? new Date(t.received_date).toLocaleDateString() : '-'}</div>{t.promised_date && <div style={{fontSize:12,color:'var(--tx3)'}}>Promised {new Date(t.promised_date).toLocaleDateString()}</div>}</div>
        </div>

        <div style={{padding:'0 16px 14px'}}>
          <div style={{background:'var(--sur2)',borderRadius:6,padding:'10px 12px'}}>
            <div style={{fontSize:11,color:'var(--tx3)',marginBottom:3}}>Reported fault</div>
            <div style={{fontSize:13}}>{t.problem_description}</div>
            {t.unlock_code && <div style={{fontSize:12,color:'var(--tx3)',marginTop:6}}>Unlock: {t.unlock_code}</div>}
            {t.accessories && <div style={{fontSize:12,color:'var(--tx3)'}}>Accessories: {t.accessories}</div>}
            {t.condition_on_intake && <div style={{fontSize:12,color:'var(--tx3)'}}>Condition: {t.condition_on_intake}</div>}
          </div>
          {t.resolution_note && (
            <div style={{background:'var(--sur2)',borderRadius:6,padding:'10px 12px',marginTop:8}}>
              <div style={{fontSize:11,color:'var(--tx3)',marginBottom:3}}>Work done</div>
              <div style={{fontSize:13}}>{t.resolution_note}</div>
            </div>
          )}
        </div>

        <div style={{padding:'0 16px 14px'}}>
          {!showLines && (
            <div style={{background:'var(--sur2)',borderRadius:6,padding:'14px 16px',textAlign:'center',color:'var(--tx3)',fontSize:13}}>
              Record the diagnosis first, then add the parts and labour the repair needs.
            </div>
          )}
          {showLines && <div style={{fontSize:12,fontWeight:600,marginBottom:6}}>Parts used</div>}
          {showLines && <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr style={{background:'var(--sur2)'}}>
              <th style={{padding:'6px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'left'}}>Product</th>
              <th style={{padding:'6px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Qty</th>
              <th style={{padding:'6px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Price</th>
              <th style={{padding:'6px 10px',fontSize:10,fontWeight:600,color:'var(--tx3)',textTransform:'uppercase',textAlign:'right'}}>Total</th>
              <th style={{width:30}} className="no-print"></th>
            </tr></thead>
            <tbody>
              {(t.parts||[]).map((p:any) => (
                <tr key={p.id} style={{borderBottom:'1px solid var(--bdr)'}}>
                  <td style={{padding:'7px 10px',fontSize:12}}>{p.product_name}<div style={{fontSize:11,color:'var(--tx3)'}}>{p.location_name}</div></td>
                  <td style={{padding:'7px 10px',textAlign:'right',fontFamily:'monospace',fontSize:12}}>{Number(p.qty)}</td>
                  <td style={{padding:'7px 10px',textAlign:'right',fontFamily:'monospace',fontSize:12}}>{fmt(p.unit_price)}</td>
                  <td style={{padding:'7px 10px',textAlign:'right',fontFamily:'monospace',fontSize:12}}>{fmt(p.line_total)}</td>
                  <td className="no-print" style={{padding:'7px 4px',textAlign:'center'}}>
                    {!isCollected && <button className="btn btn-secondary btn-sm" style={{padding:'2px 6px'}} onClick={async () => { await serviceTicketsApi.removePart(ticketId,p.id); showToast('Part removed'); load(); }}>x</button>}
                  </td>
                </tr>
              ))}
              {!isCollected && (
                <tr className="no-print">
                  <td style={{padding:'6px 6px'}}>
                    <ProductPicker value={partForm.product_id} initialLabel={partForm.product_name} locationId={partForm.location_id} fontSize={12}
                      onSelect={(p:any) => setPartForm(f=>({...f, product_id:String(p.id), product_name:p.name, unit_price:String(p.selling_price ?? 0)}))} />
                    <select className="form-control" style={{marginTop:4,fontSize:11}} value={partForm.location_id} onChange={e => setPartForm(f=>({...f,location_id:e.target.value}))}>
                      <option value="">Select location</option>
                      {locations.map((l:any) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </td>
                  <td style={{padding:'6px 4px'}}><input className="form-control mono" style={{textAlign:'right'}} value={partForm.qty} onChange={e => setPartForm(f=>({...f,qty:e.target.value}))} /></td>
                  <td style={{padding:'6px 4px'}}><input className="form-control mono" style={{textAlign:'right'}} value={partForm.unit_price} onChange={e => setPartForm(f=>({...f,unit_price:e.target.value}))} /></td>
                  <td colSpan={2} style={{padding:'6px 4px'}}><button className="btn btn-secondary btn-sm" onClick={addPart} disabled={saving}>Add part</button></td>
                </tr>
              )}
            </tbody>
          </table>}
        </div>

        <div style={{padding:'0 16px 14px'}}>
          {showLines && <div style={{fontSize:12,fontWeight:600,marginBottom:6}}>Labour</div>}
          {showLines && <table style={{width:'100%',borderCollapse:'collapse'}}>
            <tbody>
              {(t.labour||[]).map((l:any) => (
                <tr key={l.id} style={{borderBottom:'1px solid var(--bdr)'}}>
                  <td style={{padding:'7px 10px',fontSize:12}}>{l.description}{l.performed_by_name && <span style={{fontSize:11,color:'var(--tx3)'}}> — {l.performed_by_name}</span>}</td>
                  <td style={{padding:'7px 10px',textAlign:'right',fontFamily:'monospace',fontSize:12,width:110}}>{fmt(l.amount)}</td>
                  <td className="no-print" style={{width:30,textAlign:'center'}}>
                    {!isCollected && <button className="btn btn-secondary btn-sm" style={{padding:'2px 6px'}} onClick={async () => { await serviceTicketsApi.removeLabour(ticketId,l.id); showToast('Labour removed'); load(); }}>x</button>}
                  </td>
                </tr>
              ))}
              {!isCollected && (
                <tr className="no-print">
                  <td style={{padding:'6px 4px'}}>
                    <ServicePicker services={services} value={labourForm.name} onSelect={(svc: any) => setLabourForm(f => ({ ...f, product_id: String(svc.id), name: svc.name, amount: String(svc.selling_price ?? 0) }))} />
                  </td>
                  <td style={{padding:'6px 4px'}}><input className="form-control mono" style={{textAlign:'right'}} placeholder="Amount" value={labourForm.amount} onChange={e => setLabourForm(f=>({...f,amount:e.target.value}))} /></td>
                  <td style={{padding:'6px 4px'}}><button className="btn btn-secondary btn-sm" onClick={addLabour} disabled={saving}>Add</button></td>
                </tr>
              )}
            </tbody>
          </table>}
        </div>

        <div style={{display:'flex',justifyContent:'flex-end',gap:24,padding:'12px 16px',borderTop:'1px solid var(--bdr)'}}>
          <div style={{textAlign:'right'}}><div style={{fontSize:11,color:'var(--tx3)'}}>Parts</div><div style={{fontSize:13,fontFamily:'monospace'}}>{fmt(t.parts_cost)}</div></div>
          <div style={{textAlign:'right'}}><div style={{fontSize:11,color:'var(--tx3)'}}>Labour</div><div style={{fontSize:13,fontFamily:'monospace'}}>{fmt(t.service_charge)}</div></div>
          <div style={{textAlign:'right'}}><div style={{fontSize:11,color:'var(--tx3)'}}>Total</div><div style={{fontSize:16,fontWeight:700,fontFamily:'monospace'}}>{fmt(t.total_amount)}</div></div>
        </div>
      </div>

      <div className="card" id="no-print" style={{marginTop:12}}>
        <div className="card-header"><span>Notes</span></div>
        <div className="card-body">
          <div style={{display:'flex',gap:8,marginBottom:10}}>
            <input className="form-control" placeholder="Add a note..." value={noteText} onChange={e => setNoteText(e.target.value)} onKeyDown={e => { if(e.key==='Enter') addNote(); }} />
            <button className="btn btn-secondary" onClick={addNote}>Add</button>
          </div>
          {(t.notes||[]).map((n:any) => (
            <div key={n.id} style={{padding:'6px 0',borderBottom:'1px solid var(--bdr)'}}>
              <div style={{fontSize:12}}>{n.note}</div>
              <div style={{fontSize:11,color:'var(--tx3)'}}>{n.created_by_name} — {new Date(n.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>
      </>}
    </div>
  );
};

export default TicketDetail;
